import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  CalendarCheck,
  GraduationCap,
  Headset,
  LogOut,
  User,
  UserCircle,
  UsersRound,
} from "lucide-react";
import type { Role } from "../types";

interface MenuLink {
  to: string;
  labelKey: string;
  icon: LucideIcon;
}

// With no bottom bar, this menu links to the app's main screens. The PD-days
// entry is shared: facilitators see the ones they run, teachers see the ones
// their institute is attending, so only the label flips with role.
function linksFor(role: Role): MenuLink[] {
  return [
    {
      to: "/pd-days",
      labelKey: role === "teacher" ? "navPDDays" : "navFacilitator",
      icon: UsersRound,
    },
    ...(role === "facilitator"
      ? [{ to: "/training", labelKey: "navTraining", icon: GraduationCap }]
      : []),
    ...(role === "teacher" ? [{ to: "/history", labelKey: "progress", icon: BarChart3 }] : []),
    { to: "/helpline", labelKey: "navHelpline", icon: Headset },
    { to: "/schedule", labelKey: "navSchedule", icon: CalendarCheck },
    { to: "/profile", labelKey: "navProfile", icon: UserCircle },
  ];
}

const itemClass =
  "flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-app-field";

export function AccountMenu({
  role,
  onLogout,
  showHelpline = true,
}: {
  role: Role;
  onLogout: () => void;
  showHelpline?: boolean;
}) {
  const { t } = useTranslation();
  const links = linksFor(role).filter((link) => showHelpline || link.to !== "/helpline");
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent) {
      if (container.current && !container.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        className="grid h-8 w-8 place-items-center rounded-full bg-accent-brand text-content-on-accent transition-opacity active:opacity-80"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("account")}
      >
        <User size={18} />
      </button>

      {open && (
        <div
          className="absolute end-0 top-11 z-50 w-52 rounded-xl bg-surface p-1.5 shadow-card"
          role="menu"
        >
          {links.map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              role="menuitem"
              className={({ isActive }) =>
                `${itemClass} ${isActive ? "font-semibold text-accent" : "text-app-copy"}`
              }
              onClick={() => setOpen(false)}
            >
              <Icon size={17} strokeWidth={1.8} />
              {t(labelKey)}
            </NavLink>
          ))}

          <div className="my-1.5 h-px bg-app-line" />

          <button
            type="button"
            role="menuitem"
            className={`${itemClass} w-full font-semibold text-accent-strong`}
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOut size={17} strokeWidth={1.8} />
            {t("signOut")}
          </button>
        </div>
      )}
    </div>
  );
}
